/**
 * BattleCode Demo v1.0.0 — Tank 数值单一真相源
 *
 * FR-018 ADR-018.1:
 *   之前 HP/DMG/Range/CD/vision_range/accuracy 在 6 处硬编码
 *   （tank.json + engine-scheduler.ts + screen-4-battle.ts + ai-bot.py × 2 + pre-flight-scenarios.ts）
 *   改一处忘改 5 处 = 100% bug 概率
 *
 *   现在：所有 tank 数值从这里导出，引擎/渲染/Python 注入均 import
 *   TANK_STATS 是编译时单一真相源；tank.json 是 schema 档案。
 *   validateTankStats() 在 main.ts 启动时校验二者一致性，不一致时 console.warn。
 *
 * demo-gdd 锁定值（2026-07-05 v1.0.2 修订 · FR-022）：
 *   HP 150 / DMG 20 / Range 6 / Speed 1（隐式 1 tick 1 格）/ CD 30 / vision 10
 *   玩家 accuracy 0.5 / AI accuracy 0.5
 *
 * GDD §5.2 LOCK：距离一律用切比雪夫（8 方向 1 格）
 * GDD §6.1.5 LOCK：move 切比雪夫距离 = 1
 * GDD §6.3 信息博弈（FR-022 ADR-022.1）：vision 10 = 双方 spawn 距离 24 时推 7 步 = 14 tick ≈ 0.47s 进视野
 * GDD §12.3 LOCK：tank 不重叠（碰撞撞停）
 * GDD §13.4 LOCK：HP 节点切分 PHASE
 *   - ENDGAME: minHp < maxHp * 0.25
 *   - CLIMAX:  |hpDiff| >= maxHp * 0.5
 *   - CONTACT: chebyshevDist <= range
 *   - OPENING: 默认
 */

export const TANK_STATS = {
  max_health: 150,
  damage: 20,
  range: 6,
  attack_cooldown_ticks: 30,
  vision_range: 10, // 切比雪夫（FR-022 ADR-022.1：5 → 10，激活信息博弈）
  // 命中率（demo 玩家/AI 都是 0.5）
  player_accuracy: 0.5,
  ai_accuracy: 0.5,
  // 移动：每 tick 切比雪夫 1 格（GDD §6.1.5）
  move_speed: 1,
  // 地图默认尺寸（demo 锁 25×25）
  default_map_size: { width: 25, height: 25 },
} as const;

/** 死亡时显示的 HP 文本（HP=0 → "DESTROYED"，>0 → "{hp} / {max}"） */
export function formatHp(hp: number, maxHp: number): string {
  if (hp <= 0) return "DESTROYED";
  return `${hp} / ${maxHp}`;
}

/** HP 百分比 → "low" (<25%) / "mid" (25-50%) / "high" (>50%) */
export function hpPhase(hp: number, maxHp: number): "low" | "mid" | "high" {
  const pct = hp / maxHp;
  if (pct < 0.25) return "low";
  if (pct < 0.5) return "mid";
  return "high";
}

/** GDD §13.4 LOCK：HP 节点切分 PHASE（基于双方 HP + 切比雪夫距离） */
export type BattlePhase = "OPENING" | "CONTACT" | "CLIMAX" | "ENDGAME";

export function computePhase(
  selfHp: number,
  enemyHp: number,
  selfPos: { x: number; y: number },
  enemyPos: { x: number; y: number },
  maxHp: number = TANK_STATS.max_health,
  range: number = TANK_STATS.range
): BattlePhase {
  const minHp = Math.min(selfHp, enemyHp);
  const hpDiff = Math.abs(selfHp - enemyHp);
  const distance = chebyshevDistance(selfPos, enemyPos);

  // 阶段可跳：ENDGAME > CLIMAX > CONTACT > OPENING（按"最严酷"优先）
  if (minHp < maxHp * 0.25) return "ENDGAME";
  if (hpDiff >= maxHp * 0.5) return "CLIMAX";
  if (distance <= range) return "CONTACT";
  return "OPENING";
}

/** 切比雪夫距离（8 方向网格常用，对应 tank.range=6 的"6 格内任意方向"） */
export function chebyshevDistance(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

/**
 * P2-#2: 启动时校验 TANK_STATS 与 tank.json 一致性。
 * tank.json 是数据档案(schema source)，TANK_STATS 是编译时单一真相源。
 * 不一致时 console.warn 但不阻断启动（TANK_STATS 优先级更高）。
 */
export async function validateTankStats(): Promise<boolean> {
  try {
    const resp = await fetch("/data/characters/tank.json");
    if (!resp.ok) {
      console.warn("[tank-stats] 无法加载 tank.json，跳过校验");
      return true; // 非致命
    }
    const json = await resp.json();
    const s = json.stats;
    if (!s) {
      console.warn("[tank-stats] tank.json 缺少 stats 字段");
      return true;
    }
    const mismatches: string[] = [];
    if (s.hp !== TANK_STATS.max_health) mismatches.push(`hp: json=${s.hp} vs TANK_STATS=${TANK_STATS.max_health}`);
    if (s.damage !== TANK_STATS.damage) mismatches.push(`damage: json=${s.damage} vs TANK_STATS=${TANK_STATS.damage}`);
    if (s.range !== TANK_STATS.range) mismatches.push(`range: json=${s.range} vs TANK_STATS=${TANK_STATS.range}`);
    if (s.attack_cooldown_ticks !== TANK_STATS.attack_cooldown_ticks) mismatches.push(`cooldown: json=${s.attack_cooldown_ticks} vs TANK_STATS=${TANK_STATS.attack_cooldown_ticks}`);
    if (s.vision_range !== TANK_STATS.vision_range) mismatches.push(`vision: json=${s.vision_range} vs TANK_STATS=${TANK_STATS.vision_range}`);
    if (s.speed !== TANK_STATS.move_speed) mismatches.push(`speed: json=${s.speed} vs TANK_STATS=${TANK_STATS.move_speed}`);

    if (mismatches.length > 0) {
      console.warn("[tank-stats] ⚠️ tank.json 与 TANK_STATS 不一致:", mismatches.join("; "));
      console.warn("[tank-stats] 以 TANK_STATS 为准。请同步更新 tank.json 或 TANK_STATS。");
      return false;
    }
    return true;
  } catch (err) {
    console.warn("[tank-stats] tank.json 校验异常:", err);
    return true; // 非致命
  }
}

/** 切比雪夫距离 = 1 的 8 邻接方向（用于 move 校验归一化） */
export function clampMoveTarget(
  current: { x: number; y: number },
  target: { x: number; y: number },
  maxDist: number = TANK_STATS.move_speed
): { x: number; y: number } {
  const dx = target.x - current.x;
  const dy = target.y - current.y;
  const d = Math.max(Math.abs(dx), Math.abs(dy));
  if (d === 0) return current;
  if (d <= maxDist) return { x: target.x, y: target.y };
  // 沿 dx/dy 主轴向 target 走 maxDist 格
  const scale = maxDist / d;
  return {
    x: current.x + Math.round(dx * scale),
    y: current.y + Math.round(dy * scale),
  };
}
